import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './validate.css';

export default function ValidateTab({ clinician_uuid, apiBase }) {
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'validated'
  const [pendingItems, setPendingItems] = useState([]);
  const [validatedItems, setValidatedItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const isFetchingRef = useRef(false);
  const [reportUuid, setReportUuid] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const reportPollingRef = useRef(null);
  const hasInitializedRef = useRef(false);

  async function jsonFetch(res) {
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload.ok === false) {
      throw new Error(payload.error || res.statusText || 'Request failed');
    }
    return payload;
  }

  function mapQueueRow(row) {
    const patient = row.patient || {};
    const omicsJson = patient.Omics_JSON || [];
    // Try to find kit name from Omics_JSON by matching kitUnitId
    const kitEntry = omicsJson.find(k => k.kitUnitId === row.kitUnitID);
    const kitName = kitEntry?.kitName || row.source_panel || 'Unknown';

    return {
      id: row.id,
      patient_id: row.patient_id,
      name: patient.name || row.patient_id,
      source_panel: row.source_panel || 'Unknown',
      kit_name: kitName,
      kit_id: row.kitUnitID || row.omics_id,
      omics_id: row.omics_id,
      report_uuid: row.report_uuid || null, // Use report_uuid from API if exists
      created_at: row.created_at || null,
    };
  }

  async function fetchByType(type) {
    const res = await fetch(`${apiBase}/Clinician/validation/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, limit: 500 }),
    });
    return jsonFetch(res);
  }

  async function loadData(silent = false, isInitial = false) {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [pendingData, validatedData] = await Promise.all([
        fetchByType('pending'),
        fetchByType('validated'),
      ]);

      const mappedPending = (pendingData.items || []).map(mapQueueRow);
      const mappedValidated = (validatedData.items || []).map(mapQueueRow);

      setPendingItems(mappedPending);
      setValidatedItems(mappedValidated);

      // Only auto-select on initial load, not on polling refreshes
      if (isInitial && !hasInitializedRef.current) {
        hasInitializedRef.current = true;
        const currentList = activeTab === 'pending' ? mappedPending : mappedValidated;
        setSelected(currentList[0] || null);
      }
      // On refresh, just update the selected item's data if it still exists
      // Don't change selection if it's gone - let user know it was removed

      setLastRefreshed(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      if (!silent) setLoading(false);
      isFetchingRef.current = false;
    }
  }

  // Poll for report URL when selected item has no report
  const pollForReport = useCallback(async (item) => {
    if (!item || !item.omics_id) return;

    // Clear any existing polling
    if (reportPollingRef.current) {
      clearInterval(reportPollingRef.current);
      reportPollingRef.current = null;
    }

    // If item already has report_uuid from the list, use it directly
    if (item.report_uuid) {
      setReportUuid(item.report_uuid);
      setReportLoading(false);
      return;
    }

    setReportUuid(null);
    setReportLoading(true);

    // First, trigger report submission
    try {
      await fetch(`${apiBase}/Clinician/clinician-report-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          omics_id: item.omics_id,
          patient_id: item.patient_id,
          name: item.name,
          kitUnitID: item.kit_id,
        }),
      });
    } catch (e) {
      console.error('Report submission error:', e);
    }

    // Then start polling for report URL
    const pollFn = async () => {
      try {
        const res = await fetch(`${apiBase}/Clinician/clinician-report-find`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            omics_id: item.omics_id,
            kitUnitID: item.kit_id,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          if (data.report_uuid) {
            setReportUuid(data.report_uuid);
            setReportLoading(false);
            if (reportPollingRef.current) {
              clearInterval(reportPollingRef.current);
              reportPollingRef.current = null;
            }
          }
        }
      } catch (e) {
        console.error('Report polling error:', e);
      }
    };

    // Poll immediately, then every 3 seconds
    pollFn();
    reportPollingRef.current = setInterval(pollFn, 3000);
  }, [apiBase]);

  // Cleanup report polling on unmount
  useEffect(() => {
    return () => {
      if (reportPollingRef.current) {
        clearInterval(reportPollingRef.current);
      }
    };
  }, []);

  // Start polling when selected item changes
  useEffect(() => {
    if (selected) {
      pollForReport(selected);
    } else {
      setReportUuid(null);
      setReportLoading(false);
      if (reportPollingRef.current) {
        clearInterval(reportPollingRef.current);
        reportPollingRef.current = null;
      }
    }
  }, [selected, pollForReport]);

  // Auto-poll every 10s (and run immediately)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (!cancelled) await loadData(true, false); // silent refresh, not initial
    };
    loadData(false, true); // initial load
    const id = setInterval(tick, 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  // Select first item only when switching tabs (not on data refresh)
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      const currentList = activeTab === 'pending' ? pendingItems : validatedItems;
      setSelected(currentList[0] || null);
      prevTabRef.current = activeTab;
    }
  }, [activeTab, pendingItems, validatedItems]);

  const items = activeTab === 'pending' ? pendingItems : validatedItems;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.patient_id || '').toLowerCase().includes(q) ||
      (p.source_panel || '').toLowerCase().includes(q) ||
      (p.kit_id || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  // Build the streaming URL for the report
  const reportStreamUrl = reportUuid ? `${apiBase}/Clinician/report-download/${reportUuid}` : null;

  async function markSelected(status) {
    if (!selected) return;
    if (!clinician_uuid) {
      alert('Missing clinician_uuid');
      return;
    }
    setMarking(true);
    setError(null);
    try {
      const body = {
        patient_id: selected.patient_id,
        patient_name: selected.name,
        omics_id: selected.omics_id,
        kit_id: selected.kit_id,
        kit_name: selected.kit_name,
        source_panel: selected.source_panel,
        status, // 'validated' | 'rejected'
        clinician_uuid,
      };
      const url = `${apiBase}/Clinician/validation/mark`;
      await jsonFetch(
        await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      );

      // Optimistic update - move from pending to validated
      setPendingItems((prev) => {
        const next = prev.filter((x) => x.id !== selected.id);
        setSelected(next[0] || null);
        return next;
      });
      setValidatedItems((prev) => [selected, ...prev]);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setMarking(false);
    }
  }

  const pendingCount = pendingItems.length;
  const validatedCount = validatedItems.length;

  return (
    <div className="validate-container">
      {/* Sidebar */}
      <div className="validate-sidebar">
        <div className="validate-sidebar-header">
          <div className="validate-tabs">
            <button
              className={`validate-tab ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending ({pendingCount})
            </button>
            <button
              className={`validate-tab ${activeTab === 'validated' ? 'active' : ''}`}
              onClick={() => setActiveTab('validated')}
            >
              Validated ({validatedCount})
            </button>
          </div>

          <div className="validate-toolbar">
            <input
              className="validate-search"
              type="text"
              placeholder="Search name, patient ID, kit, panel..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

        </div>

        <div className={`validate-patient-list ${loading ? 'is-loading' : ''}`}>
          {loading && (
            <div className="validate-loading">
              <i className="fas fa-circle-notch fa-spin" /> Loading…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="validate-empty-list">
              {activeTab === 'pending' ? 'No pending items.' : 'No validated items.'}
            </div>
          )}
          {!loading &&
            filtered.map((it) => (
              <div
                key={it.id}
                className={`validate-patient-item ${
                  selected?.id === it.id ? 'selected' : ''
                }`}
                onClick={() => setSelected(it)}
              >
                <div className="validate-patient-header">
                  <div className="validate-patient-name">
                    {it.name || 'Unnamed'}
                  </div>
                  <div className="validate-patient-id-badge">{it.patient_id}</div>
                </div>
    
                <div className="validate-patient-kit">
                  <span className="validate-kit-id">Kit ID: {it.kit_id}</span>
                        {it.created_at && (
                  <div className="validate-patient-queued">
                    {new Date(it.created_at).toLocaleString()}
                  </div>
                )}
                </div>
          
              </div>
            ))}
        </div>
      </div>

      {/* Main pane */}
      <div className="validate-main">
        {selected ? (
          <>
            <div className="validate-report-container">
              <div className="validate-pdf-viewer">
                {reportLoading ? (
                  <div className="validate-pdf-placeholder">
                    <i className="fas fa-circle-notch fa-spin"></i>
                    <div>Loading Report...</div>
                  </div>
                ) : reportStreamUrl ? (
                  <iframe
                    src={reportStreamUrl}
                    title="Report PDF"
                    className="validate-pdf-iframe"
                  />
                ) : (
                  <div className="validate-pdf-placeholder">
                    <i className="fas fa-file-pdf"></i>
                    <div>No Report Available</div>
                  </div>
                )}
              </div>
            </div>

            {activeTab === 'pending' && (
              <div className="validate-actions">
                <button
                  className="validate-btn validate-btn-validate"
                  onClick={() => markSelected('validated')}
                  disabled={marking}
                  title="Mark as validated"
                >
                  {marking ? (
                    <i className="fas fa-circle-notch fa-spin"></i>
                  ) : (
                    <i className="fas fa-check-circle"></i>
                  )}
                  Validate
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="validate-empty-state">
            <div>{activeTab === 'pending' ? 'Select a test to validate' : 'Select a validated test to view'}</div>
          </div>
        )}
      </div>
    </div>
  );
}
